import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';

/**
 * Nueva versión de condiciones comerciales — el CASCARÓN de la ruta
 * `console/merchants/:code/condiciones/nueva` (CONTRATO-REDISENO-ALTAS.md, «C»).
 *
 * 🔴 Desde el 2026-09-28 el formulario vive en `app-condiciones-pasos`, por pasos. Acá queda lo
 * que es de la RUTA: la miga, el título, buscar el comercio y sus versiones, crear la versión y
 * volver a «Condiciones». El formulario le devuelve el cuerpo armado por `(guardado)` y este
 * cascarón llama a `createTermsVersion`; el rechazo del servidor vuelve al formulario por
 * `[error]`, así que nada de lo cargado se pierde.
 *
 * 🔴 No es un formulario de edición: crea la versión SIGUIENTE y cierra la vigente en la fecha de
 * inicio que se elija. La versión nace SIN APROBAR: aprobar es otra acción, con su propio permiso.
 *
 * ⚠️ TRANSITORIO — `[standalone]`, `[modo]`, `[comercioProvisto]`, `[vigenteProvisto]`,
 * `[proximoNumeroProvisto]`, `(creada)` y `(cancelado)` siguen existiendo solo para que la
 * tarjeta Condiciones de `alta-solicitud` compile mientras la tenga embebida (el rediseño la saca
 * de ahí: el alta embebe `app-condiciones-pasos` directo, en «Completar mi parte»). Cuando nadie
 * la embeba, esas entradas se quitan. Por omisión se comporta como siempre: ruta propia, busca
 * sola, navega sola.
 */
@Component({
  selector: 'app-comercios-condiciones-form',
  templateUrl: './comercios-condiciones-form.component.html',
  styleUrls: ['./comercios-condiciones-form.component.css']
})
export class ComerciosCondicionesFormComponent implements OnInit {

  @Input() codigo = '';
  @Input() comercioProvisto: any = null;
  @Input() vigenteProvisto: any = null;
  @Input() proximoNumeroProvisto: number | null = null;
  @Input() standalone = true;
  @Input() modo: 'version' | 'propuesta' = 'version';
  @Output() creada = new EventEmitter<any>();
  @Output() cancelado = new EventEmitter<void>();

  get esPropuesta(): boolean { return this.modo === 'propuesta'; }

  comercio: any = null;
  /** Proveedor de servicio (aseguradora): su tarifario se guarda pero no liquida. */
  get esProveedor(): boolean { return this.comercio?.business_type === 'PROVEEDOR_SERVICIO'; }
  vigente: any = null;
  proximoNumero = 1;
  /** `?paso=<clave>`: abre el formulario en ese paso. */
  pasoInicial: string | null = null;

  loading = true;
  guardando = false;
  noEncontrado = false;
  /** El rechazo del servidor, EN LA PÁGINA (lo muestra el formulario en su barra) — cero `Swal`. */
  error = '';

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService
  ) { }

  ngOnInit(): void {
    if (!this.codigo) this.codigo = this.ruta.snapshot.paramMap.get('code') || '';
    this.pasoInicial = this.ruta.snapshot.queryParamMap.get('paso');
    // 'propuesta' (el alta): no hay comercio creado — nada que buscar.
    if (this.esPropuesta) { this.cargarProvisto(); return; }
    if (this.comercioProvisto) this.cargarProvisto();
    else this.cargar();
  }

  /** Busca el comercio y sus condiciones, por `codigo`. */
  cargar = async (): Promise<void> => {
    this.loading = true;
    try {
      const rep = await this._merchant.getTerms(this.codigo);
      this.comercio = rep?.merchant || null;
      if (!this.comercio) { this.noEncontrado = true; return; }

      const versiones = rep?.terms || [];
      this.vigente = versiones.find((v: any) =>
        !v.version?.date_end && new Date(v.version?.date_init) <= new Date()) || null;
      this.proximoNumero = versiones.length
        ? Math.max(...versiones.map((v: any) => v.version?.number || 0)) + 1 : 1;
    } catch {
      this.noEncontrado = true;
    } finally {
      this.loading = false;
    }
  };

  /** Embebida (transitorio): el comercio y la vigente —o lo ya propuesto— los trae el padre. */
  private cargarProvisto = (): void => {
    this.comercio = this.comercioProvisto;
    this.vigente = this.vigenteProvisto || null;
    this.noEncontrado = !this.esPropuesta && !this.comercio;
    this.proximoNumero = this.proximoNumeroProvisto ?? ((this.vigente?.version?.number || 0) + 1);
    this.loading = false;
  };

  /** El formulario terminó: el cuerpo viene armado con la forma de siempre. */
  crear = async (datos: any): Promise<void> => {
    // 'propuesta' (el alta, transitorio): el padre hace el `PUT` de condiciones con el cuerpo.
    if (this.esPropuesta) { this.creada.emit(datos); return; }

    this.guardando = true;
    this.error = '';
    try {
      const rep = await this._merchant.createTermsVersion(this.codigo, datos);
      this.creada.emit(rep);
      if (this.standalone) {
        const aviso = 'Versión creada. Nace sin aprobar: aprobarla es el paso siguiente, desde el historial.';
        this.router.navigate(['/console/merchants', this.codigo, 'condiciones'], { state: { aviso } });
      }
    } catch (e: any) {
      this.error = [e?.error?.msg, e?.error?.detalle].filter(Boolean).join('. ') ||
                   e?.message || 'No se pudo crear la versión. Intenta de nuevo en un momento.';
    } finally {
      this.guardando = false;
    }
  };

  volver = (): void => {
    this.cancelado.emit();
    if (this.standalone) this.router.navigate(['/console/merchants', this.codigo, 'condiciones']);
  };
}
